// CareLogix™ ORACLE — AdminOne System Architecture Live Map™ (Phase 33 §5)
// Mission-control style animated architecture map: nodes, animated connection
// lines, pulse indicators, node health, activity counters. Inspired by NASA
// mission control + Bloomberg terminals + defense intelligence systems.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import { adminArchitectureApi } from "@/api/phase33Client";

// Layout grid in viewBox coordinates (0..1000 W, 0..560 H)
const NODES = [
  { id: "frontend",     label: "Frontend",      sub: "Web / Mobile / TV / Voice",   x: 100, y: 80,  color: PALETTE.gold,       layer: "frontend" },
  { id: "gateway",      label: "API Gateway",   sub: "Auth · Rate · Membership",    x: 320, y: 80,  color: PALETTE.greenAccent, layer: "gateway"  },
  { id: "agent_swarm",  label: "Agent Swarm",   sub: "LOGIX-2 · NiSi · Predictive", x: 540, y: 80,  color: PALETTE.goldBright,  layer: "intelligence" },
  { id: "redis",        label: "Redis Cache",   sub: "Hot reads · fallback",        x: 760, y: 80,  color: PALETTE.amber,       layer: "infrastructure" },
  { id: "mongo",        label: "MongoDB",       sub: "Persistence · Audit",         x: 760, y: 220, color: PALETTE.emerald,     layer: "data"     },
  { id: "event_bus",    label: "Event Bus",     sub: "Pub/Sub · Cross-domain",      x: 540, y: 220, color: PALETTE.violet,      layer: "infrastructure" },
  { id: "websockets",   label: "WebSockets",    sub: "/ws/oracle/*",                 x: 320, y: 220, color: "#22d3ee",            layer: "infrastructure" },
  { id: "notifications",label: "Notifications", sub: "SMS · Email · Push",          x: 100, y: 220, color: PALETTE.red,         layer: "execution" },
  { id: "integrations", label: "Integrations",  sub: "Twilio · SendGrid · OANDA",   x: 100, y: 360, color: PALETTE.goldBright,   layer: "execution" },
  { id: "data_market",  label: "Market Data",   sub: "TwelveData · AV · OANDA",     x: 320, y: 360, color: PALETTE.gold,         layer: "data" },
  { id: "data_funding", label: "Funding Data",  sub: "Federal · Foundation · Africa",x:540, y: 360, color: PALETTE.greenAccent,  layer: "data" },
  { id: "data_intel",   label: "Public Intel",  sub: "SEC · FDA · Filings",          x: 760, y: 360, color: PALETTE.amber,       layer: "data" },
];

const EDGES = [
  ["frontend", "gateway"],
  ["gateway", "agent_swarm"],
  ["agent_swarm", "redis"],
  ["agent_swarm", "event_bus"],
  ["event_bus", "mongo"],
  ["event_bus", "websockets"],
  ["websockets", "frontend"],
  ["event_bus", "notifications"],
  ["notifications", "integrations"],
  ["agent_swarm", "data_market"],
  ["agent_swarm", "data_funding"],
  ["agent_swarm", "data_intel"],
  ["data_market", "mongo"],
  ["data_funding", "mongo"],
];

function nodeById(id) { return NODES.find((n) => n.id === id); }

function StatusDot({ ok }) {
  return (
    <motion.span
      style={{
        width: 8, height: 8, borderRadius: "50%",
        background: ok ? PALETTE.greenAccent : PALETTE.red,
        boxShadow: ok ? `0 0 10px ${PALETTE.greenAccent}` : `0 0 10px ${PALETTE.red}`,
        display: "inline-block",
      }}
      animate={{ opacity: [1, 0.4, 1] }}
      transition={{ duration: 1.6, repeat: Infinity }}
    />
  );
}

function NodeHealth({ data, id }) {
  // Map node id → health predicate from snapshot
  if (id === "redis")        return data?.cache?.status === "ok" || data?.cache?.backend === "local";
  if (id === "mongo")        return true; // We trust mongo health from server.py status
  if (id === "agent_swarm")  return Boolean(data?.gateway);
  if (id === "event_bus")    return data?.event_bus?.status !== undefined;
  if (id === "websockets")   return true;
  if (id === "notifications")return Boolean(data?.providers?.twilio || data?.providers?.sendgrid);
  if (id === "integrations") return Boolean(data?.providers);
  return true;
}

export default function AdminSystemArchitecture({ testid = "admin-system-architecture" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adminArchitectureApi.snapshot();
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) setError("AdminOne authentication required.");
      else setError("Could not load system architecture.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 999), 60);
    return () => clearInterval(id);
  }, []);

  const W = 1000, H = 440;

  const counters = useMemo(() => ({
    cacheHits:  data?.cache?.hits   || 0,
    cacheMiss:  data?.cache?.misses || 0,
    gatewayReq: data?.gateway?.total_requests || 0,
    eventCount: Object.values(data?.event_bus?.counts || {}).reduce((a, b) => a + b, 0),
  }), [data]);

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>PHASE 33 · MISSION CONTROL</div>
          <h2 style={S.title} data-testid={`${testid}-title`}>System Architecture Live Map™</h2>
          <p style={S.subtitle}>{NODES.length} nodes · {EDGES.length} edges · auto-refresh 10s · animated flow</p>
        </div>
        <div style={S.kpis} data-testid={`${testid}-kpis`}>
          <span style={S.kpi}><span style={S.kpiLbl}>Cache hits</span><span style={S.kpiVal}>{counters.cacheHits}</span></span>
          <span style={S.kpi}><span style={S.kpiLbl}>Cache miss</span><span style={S.kpiVal}>{counters.cacheMiss}</span></span>
          <span style={S.kpi}><span style={S.kpiLbl}>Gateway req</span><span style={S.kpiVal}>{counters.gatewayReq}</span></span>
          <span style={S.kpi}><span style={S.kpiLbl}>Events</span><span style={S.kpiVal}>{counters.eventCount}</span></span>
          <button type="button" onClick={load} style={S.refresh} data-testid={`${testid}-refresh`}>↻ Refresh</button>
        </div>
      </header>

      {error && <div style={S.err} data-testid={`${testid}-error`}>{error}</div>}
      {loading && !data && <div style={S.loading}>Probing system layers…</div>}

      <div style={S.mapWrap}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" data-testid={`${testid}-svg`}>
          <defs>
            <linearGradient id={`${testid}-grad`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={PALETTE.gold}      stopOpacity="0.95" />
              <stop offset="100%" stopColor={PALETTE.greenAccent} stopOpacity="0.95" />
            </linearGradient>
          </defs>

          {/* Backdrop grid */}
          {Array.from({ length: 11 }).map((_, i) => (
            <line key={`vx${i}`} x1={(W / 10) * i} x2={(W / 10) * i} y1="0" y2={H} stroke="rgba(197,160,89,0.05)" />
          ))}
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={`hz${i}`} x1="0" x2={W} y1={(H / 4) * i} y2={(H / 4) * i} stroke="rgba(197,160,89,0.05)" />
          ))}

          {/* Edges with animated flow particles */}
          {EDGES.map(([a, b], idx) => {
            const A = nodeById(a); const B = nodeById(b);
            if (!A || !B) return null;
            const flowT = ((tick + idx * 11) % 100) / 100;
            const px = A.x + (B.x - A.x) * flowT;
            const py = A.y + (B.y - A.y) * flowT;
            return (
              <g key={`${a}-${b}`}>
                <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={`url(#${testid}-grad)`} strokeOpacity="0.45" strokeWidth="1.2" />
                <circle cx={px} cy={py} r="3" fill={PALETTE.goldBright}>
                  <title>{a} → {b}</title>
                </circle>
              </g>
            );
          })}

          {/* Nodes */}
          {NODES.map((n) => {
            const ok = NodeHealth({ data, id: n.id });
            return (
              <g key={n.id} data-testid={`${testid}-node-${n.id}`}>
                {/* outer pulse ring */}
                <motion.circle
                  cx={n.x} cy={n.y} r="34"
                  fill="none" stroke={n.color} strokeWidth="1"
                  animate={{ r: [28, 38, 28], opacity: [0.55, 0.0, 0.55] }}
                  transition={{ duration: 2.2 + (n.x % 7) * 0.1, repeat: Infinity }}
                />
                <circle cx={n.x} cy={n.y} r="22" fill="#040608" stroke={n.color} strokeWidth="2" />
                <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="11" fontFamily="'Space Mono', monospace" fontWeight="800" fill={n.color}>
                  {n.label.toUpperCase().slice(0, 6)}
                </text>
                <text x={n.x} y={n.y + 52} textAnchor="middle" fontSize="10" fontFamily="'Figtree', sans-serif" fill={PALETTE.slate3}>{n.label}</text>
                <text x={n.x} y={n.y + 66} textAnchor="middle" fontSize="8" fontFamily="'Space Mono', monospace" fill={PALETTE.slate5}>{n.sub}</text>
                <circle cx={n.x + 18} cy={n.y - 18} r="4" fill={ok ? PALETTE.greenAccent : PALETTE.red}>
                  <title>{ok ? "healthy" : "degraded"}</title>
                </circle>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Layers list (legacy detail) */}
      {data?.layers && (
        <div style={S.layersGrid} data-testid={`${testid}-layers`}>
          {Object.entries(data.layers).map(([layer, items]) => (
            <article key={layer} style={S.layerCard} data-testid={`${testid}-layer-${layer}`}>
              <header style={S.layerHead}>
                <h4 style={S.layerTitle}>{layer.replace(/_/g, " ").toUpperCase()}</h4>
                <StatusDot ok />
              </header>
              <ul style={S.layerList}>
                {items.map((it) => <li key={it} style={S.layerItem}>{it}</li>)}
              </ul>
            </article>
          ))}
        </div>
      )}

      {/* Provider status block */}
      {data?.providers && (
        <div style={S.providers} data-testid={`${testid}-providers`}>
          {Object.entries(data.providers).map(([p, ok]) => (
            <span key={p} style={{ ...S.providerPill, color: ok ? PALETTE.greenAccent : PALETTE.amber, borderColor: ok ? PALETTE.greenAccent : PALETTE.amber }}>
              {p.toUpperCase()} {ok ? "LIVE" : "STANDBY"}
            </span>
          ))}
        </div>
      )}

      <footer style={S.foot}>Live snapshot from /api/adminone/system/architecture · Compliance Lock™ · Operator-only view.</footer>
    </section>
  );
}

const S = {
  shell:     { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 18, display: "flex", flexDirection: "column", gap: 14, color: PALETTE.slate3 },
  head:      { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" },
  eyebrow:   { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.24em" },
  title:     { margin: "4px 0 0", color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.04em" },
  subtitle:  { margin: "4px 0 0", color: PALETTE.slate4, fontSize: 12 },
  kpis:      { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  kpi:       { display: "inline-flex", flexDirection: "column", padding: "4px 10px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 8 },
  kpiLbl:    { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.12em" },
  kpiVal:    { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 14, fontWeight: 800 },
  refresh:   { appearance: "none", background: "transparent", border: `1px dashed ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "5px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  err:       { color: PALETTE.red, fontSize: 12.5, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8 },
  loading:   { color: PALETTE.slate4, fontSize: 12.5 },
  mapWrap:   { background: "radial-gradient(ellipse at 50% 50%, rgba(15,45,30,0.40), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 6 },
  layersGrid:{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 },
  layerCard: { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  layerHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  layerTitle:{ margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.10em" },
  layerList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 3 },
  layerItem: { color: PALETTE.slate3, fontSize: 11.5 },
  providers: { display: "flex", flexWrap: "wrap", gap: 6 },
  providerPill: { fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px solid", letterSpacing: "0.08em" },
  foot:      { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
